<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const isLoaded = ref(false);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments, status } =
  useMessageContext();
const inProgress = computed(() => status?.value === 'progress');

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const handleLoaded = () => {
  isLoaded.value = true;
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const showError = computed(() => hasError.value && !inProgress.value);
const isLoading = computed(() => !isLoaded.value && !showError.value);
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <!-- Loading skeleton -->
    <div v-if="isLoading" class="rounded-lg overflow-hidden">
      <div
        class="w-56 h-40 bg-n-alpha-2 rounded-lg animate-pulse flex items-center justify-center"
      >
        <Icon
          icon="i-lucide-video"
          class="size-8 text-n-slate-9 animate-bounce"
        />
      </div>
    </div>
    <!-- Error state -->
    <div
      v-if="showError"
      class="flex items-center gap-1 text-center rounded-lg"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @loadeddata="handleLoaded"
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
